import {
  Input,
  DatePicker,
  InputPhone,
  Label,
  Select,
  Radio,
  Tooltip,
  InfoIcon,
} from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import {
  ProfileCustomerStatusEnum,
  ProfileTypeEnum,
  ProfileCustomerTypeEnum,
} from '@/types'
import { useAuth } from '@/providers'
import { isPendingApprovalQuotationApprover } from '@/helpers'

import type { ProfileFormProps } from './profile-form.type'
import styles from './profile-form.module.css'

export const ProfileForm = ({
  onChangeValues,
  values,
  errors,
  usersData,
}: ProfileFormProps) => {
  const { t } = useTranslation('screenProfileList')
  const { t: tEnums } = useTranslation('enums')

  const { whoAmI } = useAuth()

  const onHandleValues = (key: string, value: string | number | boolean) => {
    onChangeValues({ ...values, [key]: value })
  }

  const hasPendingApprovalPermission = isPendingApprovalQuotationApprover(
    whoAmI?.email || '',
  )

  const rawCustomerStatusOptions = Object.values(ProfileCustomerStatusEnum).map(
    (status) => ({
      label: tEnums(`profile_customer_status_${status}`),
      value: status,
    }),
  )

  const customerStatusOptions = rawCustomerStatusOptions.filter((option) => {
    if (
      !hasPendingApprovalPermission &&
      option.value === ProfileCustomerStatusEnum.churn &&
      values?.customerStatus !== ProfileCustomerStatusEnum.churn
    ) {
      return false
    }
    return true
  })

  const cannotEditCustomerStatus =
    !hasPendingApprovalPermission &&
    values?.customerStatus === ProfileCustomerStatusEnum.churn

  return (
    <div className={styles.core}>
      <div>
        <Label>{t('profileFormEmailLabel')}</Label>
        <Input
          placeholder={t('profileFormEmailPlaceholder')}
          value={values?.email}
          onChange={(newValue) => onHandleValues('email', newValue)}
          error={errors?.email}
        />
      </div>

      <div>
        <Label>{t('profileFormCompanyNameLabel')}</Label>
        <Input
          placeholder={t('profileFormCompanyNamePlaceholder')}
          value={values?.companyName}
          onChange={(newValue) => onHandleValues('companyName', newValue)}
          error={errors?.companyName}
        />
      </div>

      <div>
        <Label>{t('profileFormPhoneLabel')}</Label>
        <InputPhone
          placeholder={t('profileFormPhonePlaceholder')}
          value={values?.phone}
          onChange={(newValue) => onHandleValues('phone', newValue)}
          error={errors?.phone}
        />
      </div>

      <div>
        <Label>
          {t('profileFormMoscowLabel')} <span>{t('optionalLabel')}</span>
        </Label>
        <Select
          value={values?.moscowClassification}
          onChange={(newValue) =>
            onHandleValues('moscowClassification', newValue)
          }
          placeholder={t('profileFormMoscowPlaceholder')}
          error={errors?.moscowClassification}
          options={[
            {
              label: tEnums('profile_moscow_must'),
              value: 'must',
            },
            {
              label: tEnums('profile_moscow_should'),
              value: 'should',
            },
            {
              label: tEnums('profile_moscow_could'),
              value: 'could',
            },
            {
              label: tEnums('profile_moscow_wont'),
              value: 'wont',
            },
          ]}
        />
      </div>

      <div>
        <Label>
          {t('profileFormKeyAccountManagerLabel')}{' '}
          <span>{t('optionalLabel')}</span>
        </Label>
        <Select
          value={values?.keyAccountManagerId}
          onChange={(newValue) =>
            onHandleValues('keyAccountManagerId', newValue)
          }
          placeholder={t('profileFormKeyAccountManagerPlaceholder')}
          error={errors?.keyAccountManagerId}
          options={
            usersData?.map((user) => ({
              label: user.name,
              value: `${user.id}`,
            })) || []
          }
        />
      </div>

      <div>
        <Label>
          {t('profileFormCustomerLevelLabel')} <span>{t('optionalLabel')}</span>
        </Label>
        <Select
          value={values?.customerLevelSlug as string}
          onChange={(newValue) => onHandleValues('customerLevelSlug', newValue)}
          placeholder={t('profileFormCustomerLevelPlaceholder')}
          error={errors?.customerLevelSlug}
          options={[
            {
              label: tEnums('profile_customer_uql'),
              value: 'UQL',
            },
            {
              label: tEnums('profile_customer_mql'),
              value: 'MQL',
            },
            {
              label: tEnums('profile_customer_sql'),
              value: 'SQL',
            },
            {
              label: tEnums('profile_customer_customer'),
              value: 'customer',
            },
            {
              label: tEnums('profile_customer_disqualified'),
              value: 'disqualified',
            },
          ]}
        />
      </div>

      <div>
        <Label>
          {t('profileFormCustomerStatusLabel')}{' '}
          <span>{t('optionalLabel')}</span>
          {cannotEditCustomerStatus && (
            <Tooltip
              text={t('profileFormCustomerStatusTooltip')}
              widthText="16rem"
              orientation="left"
            >
              <InfoIcon className={styles['info-icon']} />
            </Tooltip>
          )}
        </Label>
        <Select
          disabled={cannotEditCustomerStatus}
          value={values?.customerStatus}
          onChange={(newValue) => onHandleValues('customerStatus', newValue)}
          placeholder={t('profileFormCustomerStatusPlaceholder')}
          error={errors?.customerStatus}
          options={customerStatusOptions}
        />
      </div>

      {['churn', 'passive_churn'].includes(values?.customerStatus || '') && (
        <div>
          <Label>{t('profileFormChurnAtLabel')}</Label>

          <DatePicker
            value={values?.churnedAt}
            onChange={(newValue) => onHandleValues('churnedAt', newValue || '')}
            placeholder={t('profileFormChurnAtPlaceholder')}
            error={errors?.churnedAt}
          />
        </div>
      )}

      <div>
        <Label>
          {t('profileFormTypeLabel')} <span>{t('optionalLabel')}</span>
        </Label>
        <Select
          value={values?.type}
          onChange={(newValue) => onHandleValues('type', newValue)}
          placeholder={t('profileFormTypePlaceholder')}
          error={errors?.type}
          options={Object.values(ProfileTypeEnum).map((type) => ({
            label: tEnums(`profile_type_${type}`),
            value: type,
          }))}
        />
      </div>

      <div>
        <Label>{t('profileFormisFraudulentLabel')}</Label>

        <Radio
          value={!!values?.isFraudulent}
          onChange={() => onHandleValues('isFraudulent', true)}
        >
          {t('profileFormisFraudulentOptionsYes')}
        </Radio>

        <Radio
          value={!values?.isFraudulent}
          onChange={() => onHandleValues('isFraudulent', false)}
        >
          {t('profileFormisFraudulentOptionsNo')}
        </Radio>
      </div>

      <div>
        <Label>{t('profileFormCustomerTypeLabel')}</Label>
        <Select
          value={values?.customerType}
          onChange={(newValue) => onHandleValues('customerType', newValue)}
          placeholder={t('profileFormCustomerTypePlaceholder')}
          error={errors?.customerType}
          options={Object.values(ProfileCustomerTypeEnum).map((type) => ({
            label: tEnums(`profile_customer_type_${type}`),
            value: type,
          }))}
        />
      </div>
    </div>
  )
}
